import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, Loader2, Send, X } from "lucide-react";

const field = "w-full bg-[#0A0706] border border-[#D9B47A]/25 px-4 py-3 text-[15px] text-[#F2E8DF] placeholder:text-[#8F8176] focus:outline-none focus:border-[#D9B47A]";
const btn = "inline-flex items-center justify-center gap-2 rounded-full px-5 py-3 font-tt-mono text-[11px] uppercase tracking-[0.2em] transition-colors disabled:opacity-50";
const gold = `${btn} bg-[#D9B47A] text-[#0A0706] hover:bg-[#F2E8DF]`;
const line = `${btn} border border-[#D9B47A]/50 text-[#D9B47A] hover:bg-[#D9B47A] hover:text-[#0A0706]`;
const detail = (err) => (typeof err.response?.data?.detail === "string" ? err.response.data.detail : "That didn’t work. Please try again.");
const SEGMENTS = [["everyone", "Everyone with an app"], ["gates", "13 Gates initiates"], ["quantum", "Quantum initiates"], ["partners", "Partners (invited or inviting)"]];
const Panel = ({ title, testId, children }) => <section data-testid={testId} className="mt-8 border border-[#D9B47A]/20 p-6"><p className="font-display text-2xl">{title}</p>{children}</section>;
const Row = ({ k, v }) => <div className="flex justify-between gap-4 py-1.5 text-[14px] border-b border-[#D9B47A]/10"><span className="text-[#A8998C]">{k}</span><span className="text-right text-[#F2E8DF]">{v ?? "—"}</span></div>;
const day = (iso) => (iso ? new Date(iso).toLocaleDateString() : "—");

// Drawer: one initiate's purchases, activity, reflections and partner.
export const InitiateProfile = ({ id, http, withRefresh, onClose }) => {
  const [p, setP] = useState(null);
  useEffect(() => {
    withRefresh(() => http.get(`/admin/initiates/${id}/profile`)).then(({ data }) => setP(data)).catch(() => toast.error("Could not open this profile."));
  }, [id, http, withRefresh]);
  return (
    <div className="fixed inset-0 z-[80] flex justify-end bg-black/60" onClick={onClose}>
      <aside data-testid="initiate-profile" data-lenis-prevent onClick={(e) => e.stopPropagation()} className="h-full w-full max-w-md overflow-y-auto overscroll-contain bg-[#0D0908] border-l border-[#D9B47A]/20 p-6">
        <button data-testid="initiate-profile-close" onClick={onClose} aria-label="Close" className="float-right text-[#A8998C] hover:text-[#F2E8DF]"><X className="w-5 h-5" /></button>
        {!p ? <Loader2 className="w-5 h-5 animate-spin text-[#D9B47A]" /> : (
          <>
            <p data-testid="initiate-profile-name" className="font-display text-3xl">{p.name || "Initiate"}</p>
            <p className="text-[14px] text-[#A8998C] break-all">{p.email} · joined {day(p.created_at)}</p>
            <p className="mt-6 font-tt-mono text-[11px] uppercase tracking-[0.25em] text-[#D9B47A]">Apps</p>
            <Row k="13 Gates" v={p.gates.owned ? `since ${day(p.gates.since)} · ${p.gates.opened.length}/13 opened${p.gates.mastery ? " · Mastery" : ""}` : "Not owned"} />
            <Row k="Quantum" v={p.quantum.owned ? `since ${day(p.quantum.since)} · ${p.quantum.sealed.length}/13 sealed` : "Not owned"} />
            <p className="mt-6 font-tt-mono text-[11px] uppercase tracking-[0.25em] text-[#D9B47A]">Purchases</p>
            {p.purchases.length ? p.purchases.map((t, k) => <Row key={k} k={`${t.product}${t.partner ? " · partner" : ""}${t.upgrade ? " · upgrade" : ""}`} v={`$${t.amount ?? 0} · ${t.status} · ${day(t.at)}`} />) : <p className="text-[14px] text-[#A8998C]">None</p>}
            <p className="mt-6 font-tt-mono text-[11px] uppercase tracking-[0.25em] text-[#D9B47A]">Activity</p>
            <div data-testid="initiate-profile-activity">{Object.entries(p.activity).map(([k, v]) => <Row key={k} k={k.replace(/_/g, " ")} v={k === "last_seen" ? day(v) : v} />)}</div>
            <p className="mt-6 font-tt-mono text-[11px] uppercase tracking-[0.25em] text-[#D9B47A]">Partner</p>
            <Row k="Invited by" v={p.partner.invited_by?.name || p.partner.invited_by?.email} />
            <Row k="Invited" v={p.partner.invited?.name || p.partner.invited?.email} />
            <Row k="Invitation emails sent" v={p.partner.invites_sent} />
            <p className="mt-6 font-tt-mono text-[11px] uppercase tracking-[0.25em] text-[#D9B47A]">Reflections</p>
            {p.reflections.length ? p.reflections.map((r, k) => <p key={k} className="mt-2 text-[14px] text-[#CFC2B6] italic">“{r.text}” <span className="not-italic text-[#A8998C]">· {r.status}</span></p>) : <p className="text-[14px] text-[#A8998C]">None</p>}
            <p className="mt-6 font-tt-mono text-[11px] uppercase tracking-[0.25em] text-[#D9B47A]">Emails</p>
            {Object.entries(p.emails).map(([k, v]) => <Row key={k} k={k.replace(/_/g, " ")} v={typeof v === "boolean" ? (v ? "Yes" : "No") : v} />)}
          </>
        )}
      </aside>
    </div>
  );
};

const Exports = ({ http, withRefresh }) => {
  const get = async (name) => {
    try {
      const { data } = await withRefresh(() => http.get(`/admin/export/${name}.csv`, { responseType: "blob" }));
      const a = document.createElement("a");
      a.href = URL.createObjectURL(data);
      a.download = `${name}-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch {
      toast.error("Export failed.");
    }
  };
  return (
    <Panel title="Export" testId="tools-export">
      <div className="mt-4 flex flex-wrap gap-2">
        <button data-testid="tools-export-initiates" onClick={() => get("initiates")} className={line}><Download className="w-4 h-4" /> Initiates CSV</button>
        <button data-testid="tools-export-sales" onClick={() => get("sales")} className={line}><Download className="w-4 h-4" /> Sales CSV</button>
      </div>
    </Panel>
  );
};

const Broadcast = ({ http, withRefresh }) => {
  const [f, setF] = useState({ segment: "everyone", subject: "", body: "" });
  const [count, setCount] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    setCount(null);
    withRefresh(() => http.get("/admin/broadcast/count", { params: { segment: f.segment } })).then(({ data }) => setCount(data?.count ?? 0)).catch(() => {});
  }, [f.segment, http, withRefresh]);
  const send = async (e) => {
    e.preventDefault();
    if (!window.confirm(`Send “${f.subject}” to ${count} ${count === 1 ? "person" : "people"}?`)) return;
    setBusy(true);
    try {
      await withRefresh(() => http.post("/admin/broadcast", { ...f, expected: count }));
      toast.success(`Sending to ${count} initiates.`);
      setF((x) => ({ ...x, subject: "", body: "" }));
    } catch (err) {
      toast.error(detail(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Panel title="Email a group" testId="tools-broadcast">
      <form onSubmit={send} className="mt-4 grid gap-3">
        <select data-testid="tools-broadcast-segment" value={f.segment} onChange={(e) => setF((x) => ({ ...x, segment: e.target.value }))} className={field}>
          {SEGMENTS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
        <p data-testid="tools-broadcast-count" className="text-[13px] text-[#A8998C]">{count === null ? "Counting…" : `${count} ${count === 1 ? "person" : "people"} will receive it (anyone who paused Ariel’s notes is left out).`}</p>
        <input data-testid="tools-broadcast-subject" required minLength={3} maxLength={150} value={f.subject} onChange={(e) => setF((x) => ({ ...x, subject: e.target.value }))} placeholder="Subject" className={field} />
        <textarea data-testid="tools-broadcast-body" required minLength={10} maxLength={5000} rows={8} value={f.body} onChange={(e) => setF((x) => ({ ...x, body: e.target.value }))} placeholder="Your message. Leave a blank line between paragraphs. Each email begins “Beloved {first name},”." className={field} />
        <button data-testid="tools-broadcast-send" disabled={busy || !count} className={gold}>{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send</button>
      </form>
    </Panel>
  );
};

const Password = ({ http, withRefresh }) => {
  const [f, setF] = useState({ current: "", new: "", confirm: "" });
  const [busy, setBusy] = useState(false);
  const save = async (e) => {
    e.preventDefault();
    if (f.new !== f.confirm) return toast.error("The new passwords don’t match.");
    setBusy(true);
    try {
      await withRefresh(() => http.post("/admin/password", { current: f.current, new: f.new }));
      toast.success("Your password has been changed.");
      setF({ current: "", new: "", confirm: "" });
    } catch (err) {
      toast.error(detail(err));
    } finally {
      setBusy(false);
    }
    return undefined;
  };
  return (
    <Panel title="Change your password" testId="tools-password">
      <form onSubmit={save} className="mt-4 grid gap-3 max-w-md">
        <input data-testid="tools-password-current" type="password" required autoComplete="current-password" value={f.current} onChange={(e) => setF((x) => ({ ...x, current: e.target.value }))} placeholder="Current password" className={field} />
        <input data-testid="tools-password-new" type="password" required minLength={10} autoComplete="new-password" value={f.new} onChange={(e) => setF((x) => ({ ...x, new: e.target.value }))} placeholder="New password (10+ characters)" className={field} />
        <input data-testid="tools-password-confirm" type="password" required minLength={10} autoComplete="new-password" value={f.confirm} onChange={(e) => setF((x) => ({ ...x, confirm: e.target.value }))} placeholder="Confirm new password" className={field} />
        <button data-testid="tools-password-save" disabled={busy} className={gold}>{busy && <Loader2 className="w-4 h-4 animate-spin" />} Change password</button>
      </form>
    </Panel>
  );
};

// Admin "Tools" tab.
export default function AdminTools({ http, withRefresh }) {
  return <div data-testid="admin-tools" className="mt-2"><Exports http={http} withRefresh={withRefresh} /><Broadcast http={http} withRefresh={withRefresh} /><Password http={http} withRefresh={withRefresh} /></div>;
}
